import _ from 'underscore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export declare type Procedure = (...args: Array<any>) => any;
export interface ThrottledFunction<F extends Procedure> {
  (this: ThisParameterType<F>, ...args: Parameters<F>): Promise<ReturnType<F>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cancel: (reason?: any) => void;
}

interface ThrottleSettings {
  leading?: boolean | undefined;
  trailing?: boolean | undefined;
}

/**
 * Vendored throttle function as implemented by underscore@1.9.1, ported to TypeScript, see
 * https://github.com/jashkenas/underscore/blob/1.9.1/underscore.js#L842
 *
 * Returns a function, that, when invoked, will only be triggered at most once during a given window of time.
 *
 * Normally, the throttled function will run as much as it can, without ever going more than once per `wait`
 * duration; but if you'd like to disable the execution on the leading edge, pass `{leading: false}`. To
 * disable execution on the trailing edge, ditto.
 */
export default function throttle<F extends Procedure>(
  func: F,
  wait: number,
  options?: ThrottleSettings
): ThrottledFunction<F> {
  let timeout: NodeJS.Timeout | null;
  let result: Promise<ReturnType<F>>;
  let previous = 0;

  let context: ThisParameterType<F> | null;
  let args: Parameters<F> | null;

  const later = function (): void {
    previous = options?.leading === false ? 0 : _.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) {
      context = null;
      args = null;
    }
  };

  const throttled: ThrottledFunction<F> = function (...functionArgs: Parameters<F>) {
    // Our mock timing implementation depends in part on _.now
    // (see client/test/lib/TestGlobals.ts:useFakeTimers)
    const _now = _.now();
    if (!previous && options?.leading === false) {
      previous = _now;
    }

    const remaining = wait - (_now - previous);
    context = this;
    args = functionArgs;

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = _now;
      result = func.apply(context, args);
      if (!timeout) {
        context = null;
        args = null;
      }
    } else if (!timeout && options?.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }

    return result;
  };

  throttled.cancel = function () {
    if (timeout != null) {
      clearTimeout(timeout);
    }

    previous = 0;
    timeout = null;
    context = null;
    args = null;
  };

  return throttled;
}
